import { z } from 'zod';
import {
  PortfolioSummaryView,
  QueryMetadata,
  type GlobalFilters,
  type QueryResult,
} from '@aurora-pvi/contracts';
import { apiFetch } from './apiFetch';
import { filterParams } from './filterParams';

/**
 * The `portfolio` group of `Repositories`, backed by the real API
 * (`GET /portfolio/summary` in apps/api) instead of the in-memory fixture.
 *
 * This is the adapter swap the architecture was built to make boring:
 * `DataProvider` substitutes this in for one group on the `Repositories`
 * object, and `PortfolioGlassPage` — which only ever calls
 * `repositories.portfolio.getSummary(filters)` — cannot tell the difference.
 *
 * The response is parsed through the real `PortfolioSummaryView` /
 * `QueryMetadata` schemas, not cast. A cast would make a future drift between
 * the API and the contract a silent runtime bug on some field nobody happened
 * to look at; parsing makes it a thrown error the moment the response arrives,
 * which react-query already turns into the same `sourceFailure` state every
 * screen already knows how to render.
 *
 * `/portfolio/summary` does not accept `GlobalFilters` yet, so `DataProvider`
 * Filters are sent to the route, which applies them.
 */

const Envelope = z.object({ data: PortfolioSummaryView, meta: QueryMetadata });


export function createHttpPortfolioRepository(baseUrl: string) {
  return {
    async getSummary(
      filters: GlobalFilters,
      groupBy?: string,
    ): Promise<QueryResult<PortfolioSummaryView>> {
      // The composition chart's grouping travels with the request rather than
      // being applied in the browser: each grouping is a different set of arcs
      // computed from different rows, not one shape relabelled.
      const query = new URLSearchParams(filterParams(filters).toString());
      if (groupBy) query.set('groupBy', groupBy);
      const response = await apiFetch(`${baseUrl}/portfolio/summary?${query.toString()}`);
      if (!response.ok) {
        throw new Error(`GET /portfolio/summary failed: ${response.status} ${response.statusText}`);
      }
      const json: unknown = await response.json();
      return Envelope.parse(json);
    },
  };
}
